import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  LinearProgress,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Alert,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Slider,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Plus,
  AlertTriangle,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  MoreVertical,
  Edit2,
  Trash2,
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  CheckCircle2,
  Circle,
  Clock,
  User,
  AlertOctagon,
  Info,
} from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import ConfirmationModal from '@/shared/components/modals/ConfirmationModal';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';

// Types
export interface Risk {
  id: string;
  title: string;
  description: string;
  category: RiskCategory;
  probability: 1 | 2 | 3 | 4 | 5; // 1=Very Low, 5=Very High
  impact: 1 | 2 | 3 | 4 | 5; // 1=Very Low, 5=Very High
  status: RiskStatus;
  owner: string;
  identifiedDate: string;
  dueDate?: string;
  mitigationPlan?: string;
  contingencyPlan?: string;
  triggers?: string[];
  relatedTaskIds?: string[];
  notes?: string;
}

export type RiskCategory = 
  | 'technical'
  | 'schedule'
  | 'budget'
  | 'resource'
  | 'scope'
  | 'external'
  | 'quality'
  | 'communication';

export type RiskStatus = 
  | 'identified'
  | 'analyzing'
  | 'mitigating'
  | 'monitoring'
  | 'resolved'
  | 'occurred'
  | 'closed';

interface RiskManagementProps {
  projectId: string;
  risks: Risk[];
  onUpdate?: (risks: Risk[]) => void;
}

// Risk matrix colors
const getRiskLevel = (probability: number, impact: number): { level: string; color: string; bg: string } => {
  const score = probability * impact;
  if (score >= 15) return { level: 'Critical', color: '#991b1b', bg: '#fee2e2' };
  if (score >= 10) return { level: 'High', color: '#c2410c', bg: '#ffedd5' };
  if (score >= 5) return { level: 'Medium', color: '#a16207', bg: '#fef3c7' };
  return { level: 'Low', color: '#166534', bg: '#dcfce7' };
};

// Category config
const categoryConfig: Record<RiskCategory, { label: string; color: string; icon: React.ReactElement }> = {
  technical: { label: 'Technical', color: '#3b82f6', icon: <Activity size={14} /> },
  schedule: { label: 'Schedule', color: '#8b5cf6', icon: <Clock size={14} /> },
  budget: { label: 'Budget', color: '#10b981', icon: <TrendingDown size={14} /> },
  resource: { label: 'Resource', color: '#f59e0b', icon: <User size={14} /> },
  scope: { label: 'Scope', color: '#ef4444', icon: <Target size={14} /> },
  external: { label: 'External', color: '#6366f1', icon: <AlertOctagon size={14} /> },
  quality: { label: 'Quality', color: '#ec4899', icon: <CheckCircle2 size={14} /> },
  communication: { label: 'Communication', color: '#14b8a6', icon: <Info size={14} /> },
};

// Status config
const statusConfig: Record<RiskStatus, { label: string; color: string; bg: string }> = {
  identified: { label: 'Identified', color: '#6b7280', bg: '#f3f4f6' },
  analyzing: { label: 'Analyzing', color: '#1e40af', bg: '#dbeafe' },
  mitigating: { label: 'Mitigating', color: '#7c3aed', bg: '#ede9fe' },
  monitoring: { label: 'Monitoring', color: '#0369a1', bg: '#e0f2fe' },
  resolved: { label: 'Resolved', color: '#166534', bg: '#dcfce7' },
  occurred: { label: 'Occurred', color: '#991b1b', bg: '#fee2e2' },
  closed: { label: 'Closed', color: '#374151', bg: '#e5e7eb' },
};

const probabilityLabels = ['', 'Very Low', 'Low', 'Medium', 'High', 'Very High'];
const impactLabels = ['', 'Very Low', 'Low', 'Medium', 'High', 'Very High'];

const RiskManagement: React.FC<RiskManagementProps> = ({
  projectId,
  risks,
  onUpdate,
}) => {
  // State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRisk, setEditingRisk] = useState<Risk | null>(null);
  const [selectedRisk, setSelectedRisk] = useState<Risk | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRisk, setMenuRisk] = useState<Risk | null>(null);
  const [deleteConfirmRisk, setDeleteConfirmRisk] = useState<Risk | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'table' | 'matrix'>('table');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'technical' as RiskCategory,
    probability: 3 as Risk['probability'],
    impact: 3 as Risk['impact'],
    status: 'identified' as RiskStatus,
    owner: '',
    dueDate: null as Dayjs | null,
    mitigationPlan: '',
    contingencyPlan: '',
    triggers: '',
    notes: '',
  });

  // Calculations
  const stats = useMemo(() => {
    const total = risks.length;
    const critical = risks.filter((r) => getRiskLevel(r.probability, r.impact).level === 'Critical').length;
    const high = risks.filter((r) => getRiskLevel(r.probability, r.impact).level === 'High').length;
    const medium = risks.filter((r) => getRiskLevel(r.probability, r.impact).level === 'Medium').length;
    const low = risks.filter((r) => getRiskLevel(r.probability, r.impact).level === 'Low').length;
    const open = risks.filter((r) => !['resolved', 'closed'].includes(r.status)).length;
    const mitigating = risks.filter((r) => r.status === 'mitigating').length;
    const occurred = risks.filter((r) => r.status === 'occurred').length;

    return { total, critical, high, medium, low, open, mitigating, occurred };
  }, [risks]);

  // Filtered risks
  const filteredRisks = useMemo(() => {
    let result = [...risks];
    
    if (filterCategory !== 'all') {
      result = result.filter((r) => r.category === filterCategory);
    }
    if (filterStatus !== 'all') {
      result = result.filter((r) => r.status === filterStatus);
    }
    
    // Sort by risk score (highest first)
    return result.sort((a, b) => (b.probability * b.impact) - (a.probability * a.impact));
  }, [risks, filterCategory, filterStatus]);

  // Risk matrix data
  const matrixData = useMemo(() => {
    const matrix: Record<string, Risk[]> = {};
    
    for (let p = 5; p >= 1; p--) {
      for (let i = 1; i <= 5; i++) {
        const key = `${p}-${i}`;
        matrix[key] = risks.filter((r) => r.probability === p && r.impact === i);
      }
    }
    
    return matrix;
  }, [risks]);

  // Handlers
  const handleOpenDialog = (risk?: Risk) => {
    if (risk) {
      setEditingRisk(risk);
      setFormData({
        title: risk.title,
        description: risk.description,
        category: risk.category,
        probability: risk.probability,
        impact: risk.impact,
        status: risk.status,
        owner: risk.owner,
        dueDate: risk.dueDate ? dayjs(risk.dueDate) : null,
        mitigationPlan: risk.mitigationPlan || '',
        contingencyPlan: risk.contingencyPlan || '',
        triggers: risk.triggers?.join(', ') || '',
        notes: risk.notes || '',
      });
    } else {
      setEditingRisk(null);
      setFormData({
        title: '',
        description: '',
        category: 'technical',
        probability: 3,
        impact: 3,
        status: 'identified',
        owner: '',
        dueDate: null,
        mitigationPlan: '',
        contingencyPlan: '',
        triggers: '',
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRisk(null);
  };

  const handleSave = () => {
    const newRisk: Risk = {
      id: editingRisk?.id || `risk-${Date.now()}`,
      title: formData.title,
      description: formData.description,
      category: formData.category,
      probability: formData.probability,
      impact: formData.impact,
      status: formData.status,
      owner: formData.owner,
      identifiedDate: editingRisk?.identifiedDate || new Date().toISOString(),
      dueDate: formData.dueDate?.toISOString(),
      mitigationPlan: formData.mitigationPlan || undefined,
      contingencyPlan: formData.contingencyPlan || undefined,
      triggers: formData.triggers ? formData.triggers.split(',').map((t) => t.trim()) : undefined,
      notes: formData.notes || undefined,
    };

    let updatedRisks: Risk[];
    if (editingRisk) {
      updatedRisks = risks.map((r) => (r.id === editingRisk.id ? newRisk : r));
    } else {
      updatedRisks = [...risks, newRisk];
    }

    onUpdate?.(updatedRisks);
    handleCloseDialog();
  };

  const handleDelete = (riskId: string) => {
    const updatedRisks = risks.filter((r) => r.id !== riskId);
    onUpdate?.(updatedRisks);
    setMenuAnchor(null);
  };

  const handleUpdateStatus = (riskId: string, status: RiskStatus) => {
    const updatedRisks = risks.map((r) => (r.id === riskId ? { ...r, status } : r));
    onUpdate?.(updatedRisks);
    setMenuAnchor(null);
  };

  const getStatusIcon = (status: RiskStatus) => {
    switch (status) {
      case 'resolved':
        return <ShieldCheck size={16} color="#22c55e" />;
      case 'occurred':
        return <ShieldX size={16} color="#ef4444" />;
      case 'mitigating':
        return <ShieldAlert size={16} color="#f59e0b" />;
      default:
        return <Shield size={16} color="#6b7280" />;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Risk Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Identify, assess, and mitigate project risks
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant={viewMode === 'table' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setViewMode('table')}
          >
            Table
          </Button>
          <Button
            variant={viewMode === 'matrix' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setViewMode('matrix')}
          >
            Matrix
          </Button>
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={() => handleOpenDialog()}
            size="small"
          >
            Add Risk
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f8fafc' }}>
            <Typography variant="h5" fontWeight={700} color="primary.main">
              {stats.total}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Risks
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fee2e2' }}>
            <Typography variant="h5" fontWeight={700} color="#991b1b">
              {stats.critical}
            </Typography>
            <Typography variant="caption" color="#991b1b">
              Critical
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffedd5' }}>
            <Typography variant="h5" fontWeight={700} color="#c2410c">
              {stats.high}
            </Typography>
            <Typography variant="caption" color="#c2410c">
              High
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fef3c7' }}>
            <Typography variant="h5" fontWeight={700} color="#a16207">
              {stats.medium}
            </Typography>
            <Typography variant="caption" color="#a16207">
              Medium
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#dcfce7' }}>
            <Typography variant="h5" fontWeight={700} color="#166534">
              {stats.low}
            </Typography>
            <Typography variant="caption" color="#166534">
              Low
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: stats.occurred > 0 ? '#fee2e2' : '#f3f4f6' }}>
            <Typography variant="h5" fontWeight={700} color={stats.occurred > 0 ? '#991b1b' : '#374151'}>
              {stats.occurred}
            </Typography>
            <Typography variant="caption" color={stats.occurred > 0 ? '#991b1b' : 'text.secondary'}>
              Occurred
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Matrix View */}
      {viewMode === 'matrix' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Risk Assessment Matrix
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 500 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 100 }}>
                      <Typography variant="caption" fontWeight={600}>
                        Probability ↓
                      </Typography>
                    </TableCell>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <TableCell key={i} align="center" sx={{ width: 100 }}>
                        <Typography variant="caption" fontWeight={600}>
                          {impactLabels[i]}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[5, 4, 3, 2, 1].map((p) => (
                    <TableRow key={p}>
                      <TableCell>
                        <Typography variant="caption" fontWeight={500}>
                          {probabilityLabels[p]}
                        </Typography>
                      </TableCell>
                      {[1, 2, 3, 4, 5].map((i) => {
                        const key = `${p}-${i}`;
                        const cellRisks = matrixData[key] || [];
                        const level = getRiskLevel(p, i);
                        
                        return (
                          <TableCell
                            key={key}
                            align="center"
                            sx={{
                              bgcolor: level.bg,
                              border: '1px solid #e5e7eb',
                              cursor: cellRisks.length > 0 ? 'pointer' : 'default',
                              '&:hover': cellRisks.length > 0 ? { opacity: 0.8 } : {},
                            }}
                          >
                            {cellRisks.length > 0 ? (
                              <Tooltip
                                title={
                                  <Box>
                                    {cellRisks.map((r) => (
                                      <Typography key={r.id} variant="caption" display="block">
                                        • {r.title}
                                      </Typography>
                                    ))}
                                  </Box>
                                }
                              >
                                <Chip
                                  label={cellRisks.length}
                                  size="small"
                                  sx={{
                                    bgcolor: level.color,
                                    color: 'white',
                                    fontWeight: 600,
                                    minWidth: 28,
                                    height: 28,
                                  }}
                                />
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Impact →
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <Box>
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <AppSelect
              label="Category"
              size="small"
              formControlProps={{ sx: { minWidth: 150 } }}
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <AppMenuItem value="all">All Categories</AppMenuItem>
              {Object.entries(categoryConfig).map(([key, config]) => (
                <AppMenuItem key={key} value={key}>
                  {config.label}
                </AppMenuItem>
              ))}
            </AppSelect>
            <AppSelect
              label="Status"
              size="small"
              formControlProps={{ sx: { minWidth: 120 } }}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <AppMenuItem value="all">All Status</AppMenuItem>
              {Object.entries(statusConfig).map(([key, config]) => (
                <AppMenuItem key={key} value={key}>
                  {config.label}
                </AppMenuItem>
              ))}
            </AppSelect>
          </Box>

          {/* Risks Table */}
          {filteredRisks.length === 0 ? (
            <Card sx={{ textAlign: 'center', py: 6 }}>
              <Shield size={48} color="#9ca3af" style={{ marginBottom: 16 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No risks identified
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add risks to track and mitigate potential issues
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Plus size={16} />}
                onClick={() => handleOpenDialog()}
              >
                Add First Risk
              </Button>
            </Card>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f9fafb' }}>
                    <TableCell>Risk</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="center">P × I</TableCell>
                    <TableCell>Level</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRisks.map((risk) => {
                    const level = getRiskLevel(risk.probability, risk.impact);
                    const catConfig = categoryConfig[risk.category];
                    const statConfig = statusConfig[risk.status];
                    
                    return (
                      <TableRow
                        key={risk.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelectedRisk(risk)}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getStatusIcon(risk.status)}
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {risk.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 250, display: 'block' }}>
                                {risk.description}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={catConfig.icon}
                            label={catConfig.label}
                            size="small"
                            sx={{
                              bgcolor: catConfig.color + '20',
                              color: catConfig.color,
                              '& .MuiChip-icon': { color: catConfig.color },
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600}>
                            {risk.probability} × {risk.impact} = {risk.probability * risk.impact}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={level.level}
                            size="small"
                            sx={{
                              bgcolor: level.bg,
                              color: level.color,
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statConfig.label}
                            size="small"
                            sx={{
                              bgcolor: statConfig.bg,
                              color: statConfig.color,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {risk.owner || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuAnchor(e.currentTarget);
                              setMenuRisk(risk);
                            }}
                          >
                            <MoreVertical size={16} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuRisk(null);
        }}
      >
        <MenuItem
          onClick={() => {
            if (menuRisk) handleOpenDialog(menuRisk);
            setMenuAnchor(null);
          }}
        >
          <Edit2 size={16} style={{ marginRight: 8 }} />
          Edit
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => menuRisk && handleUpdateStatus(menuRisk.id, 'mitigating')}>
          <ShieldAlert size={16} style={{ marginRight: 8 }} color="#f59e0b" />
          Mark Mitigating
        </MenuItem>
        <MenuItem onClick={() => menuRisk && handleUpdateStatus(menuRisk.id, 'resolved')}>
          <ShieldCheck size={16} style={{ marginRight: 8 }} color="#22c55e" />
          Mark Resolved
        </MenuItem>
        <MenuItem onClick={() => menuRisk && handleUpdateStatus(menuRisk.id, 'occurred')}>
          <ShieldX size={16} style={{ marginRight: 8 }} color="#ef4444" />
          Mark Occurred
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (menuRisk) {
              setDeleteConfirmRisk(menuRisk);
              setMenuAnchor(null);
            }
          }}
          sx={{ color: 'error.main' }}
        >
          <Trash2 size={16} style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRisk ? 'Edit Risk' : 'Add Risk'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Risk Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
              placeholder="e.g., Key developer leaving the project"
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              required
              placeholder="Describe the risk in detail..."
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <AppSelect
                  label="Category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as RiskCategory })}
                >
                  {Object.entries(categoryConfig).map(([key, config]) => (
                    <AppMenuItem key={key} value={key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {config.icon}
                        {config.label}
                      </Box>
                    </AppMenuItem>
                  ))}
                </AppSelect>
              </Grid>
              <Grid item xs={12} sm={6}>
                <AppSelect
                  label="Status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as RiskStatus })}
                >
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <AppMenuItem key={key} value={key}>
                      {config.label}
                    </AppMenuItem>
                  ))}
                </AppSelect>
              </Grid>
            </Grid>

            {/* Risk Assessment */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Risk Assessment
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormLabel>Probability: {probabilityLabels[formData.probability]}</FormLabel>
                  <Slider
                    value={formData.probability}
                    onChange={(_, v) => setFormData({ ...formData, probability: v as Risk['probability'] })}
                    min={1}
                    max={5}
                    step={1}
                    marks={[
                      { value: 1, label: '1' },
                      { value: 2, label: '2' },
                      { value: 3, label: '3' },
                      { value: 4, label: '4' },
                      { value: 5, label: '5' },
                    ]}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormLabel>Impact: {impactLabels[formData.impact]}</FormLabel>
                  <Slider
                    value={formData.impact}
                    onChange={(_, v) => setFormData({ ...formData, impact: v as Risk['impact'] })}
                    min={1}
                    max={5}
                    step={1}
                    marks={[
                      { value: 1, label: '1' },
                      { value: 2, label: '2' },
                      { value: 3, label: '3' },
                      { value: 4, label: '4' },
                      { value: 5, label: '5' },
                    ]}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2">Risk Score:</Typography>
                <Chip
                  label={`${formData.probability} × ${formData.impact} = ${formData.probability * formData.impact}`}
                  sx={{
                    bgcolor: getRiskLevel(formData.probability, formData.impact).bg,
                    color: getRiskLevel(formData.probability, formData.impact).color,
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={getRiskLevel(formData.probability, formData.impact).level}
                  sx={{
                    bgcolor: getRiskLevel(formData.probability, formData.impact).bg,
                    color: getRiskLevel(formData.probability, formData.impact).color,
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Risk Owner"
                  value={formData.owner}
                  onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                  fullWidth
                  placeholder="Person responsible for this risk"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Review/Due Date"
                  value={formData.dueDate}
                  onChange={(date) => setFormData({ ...formData, dueDate: date })}
                  slotProps={{
                    textField: { fullWidth: true },
                  }}
                />
              </Grid>
            </Grid>

            <TextField
              label="Mitigation Plan"
              value={formData.mitigationPlan}
              onChange={(e) => setFormData({ ...formData, mitigationPlan: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Steps to reduce the probability or impact of this risk..."
            />

            <TextField
              label="Contingency Plan"
              value={formData.contingencyPlan}
              onChange={(e) => setFormData({ ...formData, contingencyPlan: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Actions to take if the risk occurs..."
            />

            <TextField
              label="Triggers (comma-separated)"
              value={formData.triggers}
              onChange={(e) => setFormData({ ...formData, triggers: e.target.value })}
              fullWidth
              placeholder="e.g., Budget overrun by 20%, Team member resignation notice"
            />

            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Additional notes..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.title || !formData.description}
          >
            {editingRisk ? 'Save Changes' : 'Add Risk'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Risk Detail Dialog */}
      <Dialog
        open={Boolean(selectedRisk)}
        onClose={() => setSelectedRisk(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedRisk && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getStatusIcon(selectedRisk.status)}
                {selectedRisk.title}
              </Box>
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    label={getRiskLevel(selectedRisk.probability, selectedRisk.impact).level}
                    sx={{
                      bgcolor: getRiskLevel(selectedRisk.probability, selectedRisk.impact).bg,
                      color: getRiskLevel(selectedRisk.probability, selectedRisk.impact).color,
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label={categoryConfig[selectedRisk.category].label}
                    sx={{
                      bgcolor: categoryConfig[selectedRisk.category].color + '20',
                      color: categoryConfig[selectedRisk.category].color,
                    }}
                  />
                  <Chip
                    label={statusConfig[selectedRisk.status].label}
                    sx={{
                      bgcolor: statusConfig[selectedRisk.status].bg,
                      color: statusConfig[selectedRisk.status].color,
                    }}
                  />
                </Box>

                <Typography variant="body2">{selectedRisk.description}</Typography>

                <Divider />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Probability</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {probabilityLabels[selectedRisk.probability]} ({selectedRisk.probability})
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Impact</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {impactLabels[selectedRisk.impact]} ({selectedRisk.impact})
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Risk Score</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedRisk.probability * selectedRisk.impact}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Owner</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedRisk.owner || '-'}
                    </Typography>
                  </Grid>
                </Grid>

                {selectedRisk.mitigationPlan && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Mitigation Plan</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedRisk.mitigationPlan}
                    </Typography>
                  </Box>
                )}

                {selectedRisk.contingencyPlan && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Contingency Plan</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedRisk.contingencyPlan}
                    </Typography>
                  </Box>
                )}

                {selectedRisk.triggers && selectedRisk.triggers.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>Triggers</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedRisk.triggers.map((trigger, i) => (
                        <Chip key={i} label={trigger} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedRisk(null)}>Close</Button>
              <Button
                variant="outlined"
                onClick={() => {
                  handleOpenDialog(selectedRisk);
                  setSelectedRisk(null);
                }}
              >
                Edit Risk
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <ConfirmationModal
        open={!!deleteConfirmRisk}
        onClose={() => setDeleteConfirmRisk(null)}
        data={
          deleteConfirmRisk
            ? {
                title: 'Delete risk',
                message: `Delete the risk "${deleteConfirmRisk.title}"? This cannot be undone.`,
                confirmText: 'Delete',
                type: 'danger',
                onConfirm: () => {
                  if (deleteConfirmRisk) handleDelete(deleteConfirmRisk.id);
                  setDeleteConfirmRisk(null);
                },
              }
            : undefined
        }
      />
    </Box>
  );
};

export default RiskManagement;
